import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon, LockKeyholeIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { changePasswordSchema, passwordSchema } from '@shared/schemas/auth';
import { changeCurrentPassword, setInitialPassword } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const changePasswordFormSchema = changePasswordSchema.extend({
  confirmPassword: passwordSchema,
}).refine((values) => values.newPassword === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

const setPasswordFromAccountSchema = z.object({
  confirmPassword: passwordSchema,
  newPassword: passwordSchema,
}).refine((values) => values.newPassword === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type SetPasswordFormValues = z.infer<typeof setPasswordFromAccountSchema>;
type ChangePasswordFormInput = z.input<typeof changePasswordFormSchema>;
type ChangePasswordFormOutput = z.output<typeof changePasswordFormSchema>;

export function AccountPasswordForm({
  className,
  onAccountUpdated,
  ...props
}: React.ComponentProps<'div'> & {
  onAccountUpdated?: () => Promise<void>;
}) {
  const [mode, setMode] = useState<'change' | 'set'>('change');
  const changePasswordForm = useForm<ChangePasswordFormInput, undefined, ChangePasswordFormOutput>({
    defaultValues: {
      confirmPassword: '',
      currentPassword: '',
      newPassword: '',
      revokeOtherSessions: true,
    },
    resolver: zodResolver(changePasswordFormSchema),
  });
  const setPasswordForm = useForm<SetPasswordFormValues>({
    defaultValues: {
      confirmPassword: '',
      newPassword: '',
    },
    resolver: zodResolver(setPasswordFromAccountSchema),
  });

  async function handleChangePassword(values: ChangePasswordFormOutput) {
    changePasswordForm.clearErrors('root');

    const result = await changeCurrentPassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      revokeOtherSessions: true,
    });

    if (result.error) {
      if (
        typeof result.error === 'object' &&
        result.error &&
        'code' in result.error &&
        result.error.code === 'CREDENTIAL_ACCOUNT_NOT_FOUND'
      ) {
        setMode('set');
        toast.info('Ce compte n a pas encore de mot de passe. Définis-en un maintenant.');
        return;
      }

      const message = getAuthErrorMessage(result.error);
      changePasswordForm.setError('root', { message });
      toast.error(message);
      return;
    }

    changePasswordForm.reset({
      confirmPassword: '',
      currentPassword: '',
      newPassword: '',
      revokeOtherSessions: true,
    });
    if (onAccountUpdated) {
      await onAccountUpdated();
    }
    toast.success('Mot de passe mis à jour. Les autres sessions ont été révoquées.');
  }

  async function handleSetPassword(values: SetPasswordFormValues) {
    setPasswordForm.clearErrors('root');

    const result = await setInitialPassword(values.newPassword);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      setPasswordForm.setError('root', { message });
      toast.error(message);
      return;
    }

    setPasswordForm.reset({
      confirmPassword: '',
      newPassword: '',
    });
    setMode('change');
    if (onAccountUpdated) {
      await onAccountUpdated();
    }
    toast.success('Mot de passe initial défini.');
  }

  return (
    <section className={cn('rounded-[var(--radius-surface-4xl)] border border-border bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]', className)} {...props}>
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-foreground">Sécurité</h2>
        <p className="text-sm leading-6 text-muted-foreground">
          Le changement de mot de passe révoque les autres sessions actives. Si aucun mot de passe n’avait encore été
          défini pour ce compte, vous pouvez en définir un premier ici.
        </p>
      </div>

      {mode === 'change' ? (
        <form className="mt-6 flex flex-col gap-6" onSubmit={changePasswordForm.handleSubmit(handleChangePassword)}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="account-current-password">Mot de passe actuel</FieldLabel>
              <Input
                id="account-current-password"
                data-testid="account-current-password-input"
                type="password"
                autoComplete="current-password"
                aria-invalid={changePasswordForm.formState.errors.currentPassword ? 'true' : 'false'}
                {...changePasswordForm.register('currentPassword')}
              />
              <FieldError errors={[changePasswordForm.formState.errors.currentPassword]} />
            </Field>

            <div className="grid gap-5 lg:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="account-new-password">Nouveau mot de passe</FieldLabel>
                <Input
                  id="account-new-password"
                  data-testid="account-new-password-input"
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={changePasswordForm.formState.errors.newPassword ? 'true' : 'false'}
                  {...changePasswordForm.register('newPassword')}
                />
                <FieldDescription>Au moins 8 caractères.</FieldDescription>
                <FieldError errors={[changePasswordForm.formState.errors.newPassword]} />
              </Field>

              <Field>
                <FieldLabel htmlFor="account-confirm-password">Confirmation</FieldLabel>
                <Input
                  id="account-confirm-password"
                  data-testid="account-confirm-password-input"
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={changePasswordForm.formState.errors.confirmPassword ? 'true' : 'false'}
                  {...changePasswordForm.register('confirmPassword')}
                />
                <FieldError errors={[changePasswordForm.formState.errors.confirmPassword]} />
              </Field>
            </div>

            <div className="rounded-[var(--radius-surface-3xl)] border border-border bg-muted/60 p-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <LockKeyholeIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                <p className="leading-6">
                  Par sécurité, les autres sessions seront révoquées après mise à jour du mot de passe.
                </p>
              </div>
            </div>

            <FieldError errors={[changePasswordForm.formState.errors.root]} />

            <div className="flex justify-end">
              <Button data-testid="account-change-password-submit" type="submit" disabled={changePasswordForm.formState.isSubmitting}>
                {changePasswordForm.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
                Mettre à jour le mot de passe
              </Button>
            </div>
          </FieldGroup>
        </form>
      ) : (
        <form className="mt-6 flex flex-col gap-6" onSubmit={setPasswordForm.handleSubmit(handleSetPassword)}>
          <FieldGroup>
            <div className="rounded-[var(--radius-surface-3xl)] border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 dark:border-sky-900/70 dark:bg-sky-500/10 dark:text-sky-200">
              Ce mode est utile si le compte a été créé sans mot de passe initial.
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="account-set-password">Mot de passe</FieldLabel>
                <Input
                  id="account-set-password"
                  data-testid="account-set-password-input"
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={setPasswordForm.formState.errors.newPassword ? 'true' : 'false'}
                  {...setPasswordForm.register('newPassword')}
                />
                <FieldDescription>Au moins 8 caractères.</FieldDescription>
                <FieldError errors={[setPasswordForm.formState.errors.newPassword]} />
              </Field>

              <Field>
                <FieldLabel htmlFor="account-set-password-confirm">Confirmation</FieldLabel>
                <Input
                  id="account-set-password-confirm"
                  data-testid="account-set-password-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={setPasswordForm.formState.errors.confirmPassword ? 'true' : 'false'}
                  {...setPasswordForm.register('confirmPassword')}
                />
                <FieldError errors={[setPasswordForm.formState.errors.confirmPassword]} />
              </Field>
            </div>

            <FieldError errors={[setPasswordForm.formState.errors.root]} />

            <div className="flex justify-end">
              <Button data-testid="account-set-password-submit" type="submit" disabled={setPasswordForm.formState.isSubmitting}>
                {setPasswordForm.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
                Définir le mot de passe
              </Button>
            </div>
          </FieldGroup>
        </form>
      )}
    </section>
  );
}
